import { escapeRegexString, sanitizeRichText } from '@anecdotes/utils';
import { Dictionary } from '@ngrx/entity';
import {
  DataManipulationConfig,
  ElementsTree,
  FilterPropertySearchOverlap,
  FilterSearchOverlap,
  OverlapElementTree,
  SearcableRow,
  SearchableData,
} from '../../../models';

export class SearchHelper {
  static Instance = new SearchHelper();

  calculateSearchOverlaps(
    searchedValue: string,
    searchableData: Dictionary<SearchableData>
  ): Dictionary<FilterSearchOverlap> {
    if (!searchedValue || !searchableData) {
      return null;
    }

    const searchTextRegex = new RegExp(escapeRegexString(searchedValue), 'gi');
    const result: Dictionary<FilterSearchOverlap> = {};

    Object.values(searchableData)
      .filter(Boolean)
      .forEach((searchableData) => {
        const propertyOverlaps: FilterPropertySearchOverlap[] = [];

        searchableData.searchableRows.forEach((searchableRow) => {
          const match = this.getTextContent(searchableRow.propertyValue)?.match(searchTextRegex);

          if (match) {
            propertyOverlaps.push({
              propertyId: searchableRow.propertyId,
              overlapsCount: match.length,
              overlapElementTree: searchableRow.elementsTree
                ? this.searchMatchesInElementsTree(
                  searchableRow.elementsTree,
                  searchTextRegex,
                  searchableRow.propertyId,
                )
                : null,
            });
          }
        });

        if (propertyOverlaps.length) {
          result[searchableData.itemId] = {
            itemId: searchableData.itemId,
            item: searchableData.item,
            propertyOverlaps,
          };
        }
      });

    return result;
  }

  buildSearchableData(dataDictionary: Dictionary<any>, filterConfig: DataManipulationConfig): Dictionary<SearchableData> {
    if (!filterConfig.searchDefinition?.length) {
      return null;
    }

    const result: Dictionary<SearchableData> = {};

    Object.values(dataDictionary).forEach((dataItem) => {
      const searchableRows: SearcableRow[] = [];

      filterConfig.searchDefinition?.forEach((searchDef) => {
        const propertySelectorResult = searchDef.propertySelector(dataItem);
        const propertyValue = Array.isArray(propertySelectorResult)
          ? propertySelectorResult.join('\n')
          : (propertySelectorResult as string);

        let elementsTree: ElementsTree[] = null;

        if (searchDef.elementsTree) {
          elementsTree = searchDef.elementsTree(dataItem);
        }

        searchableRows.push({
          propertyId: searchDef.propertyId,
          propertyValue,
          elementsTree,
        });
      });

      result[dataItem[filterConfig.idPropertyName]] = {
        itemId: dataItem[filterConfig.idPropertyName],
        item: dataItem,
        searchableRows,
      };
    });

    return result;
  }

  private getTextContent(txt: string): string {
    const element = document.createElement('span');
    element.innerHTML = sanitizeRichText(txt);
    return element.textContent;
  }

  private searchMatchesInElementsTree(data: ElementsTree[], searchTextRegex: RegExp, propertyId: string): OverlapElementTree[] {
    return data.reduce((result, item): OverlapElementTree[] => {
      const resultItem: OverlapElementTree = {
        id: item.id,
        elementResourceType: item.elementResourceType,
        searchId: item.searchId ?? item.id,
      };

      if (item.value) {
        const itemMatches = this.getTextContent(item.value).match(searchTextRegex);

        if (itemMatches) {
          return [
            ...result,
            ...itemMatches.map(
              (_, index): OverlapElementTree => ({ ...resultItem, value: item.value, matchIndex: index, propertyId }),
            ),
          ];
        }
      }

      if (item.nestedElements) {
        const filteredNestedElements = this.searchMatchesInElementsTree(item.nestedElements, searchTextRegex, propertyId);

        if (filteredNestedElements.length > 0) {
          const nestedTree = filteredNestedElements.map(
            (elTree: OverlapElementTree): OverlapElementTree => ({
              ...resultItem,
              nestedElement: elTree,
            }),
          );

          return [...result, ...nestedTree];
        }
      }

      return result;
    }, [] as OverlapElementTree[]);
  }
}
